/*
 * Decléor — keep the cart's scroll position and keyboard focus across the
 * drawer's line-items re-render.
 * ---------------------------------------------------------------------------
 * theme.js (vendored, read-only) rebuilds the WHOLE of [data-line-items] with
 * innerHTML after every cart mutation — loadForm(), theme.js:10109. The cart
 * upsell panel is rendered inside that same snippet (cart-line-items.liquid),
 * so a quantity change throws away markup that did not change.
 *
 * Measured on a 2-item cart, one "+" click, 127.0.0.1:9292:
 *   • all 6 .cart-upsell__card nodes replaced ~700 ms after the click;
 *   • desktop .cart-upsell__viewport scrollTop 200 → 0 (352 px of overflow);
 *   • mobile [data-upsell-track] scrollLeft 390 → 0 (back to slide 1);
 *   • document.activeElement → <body>, INCLUDING when the focus was on the
 *     "+" button itself, so a keyboard user must re-Tab in for every unit;
 *   • the staggered cu-up entrance replays in full, ~870 ms of fade-in.
 *
 * The upsell list is byte-identical across a quantity change (same 6 product
 * hrefs, same line keys), which is why a signature comparison is enough to
 * tell "same content, restore it silently" from "cart really changed, let it
 * animate". When the content DID change we deliberately do nothing but move
 * focus somewhere sane — the new list is new information and should be seen.
 *
 * Scoped to the [data-line-items] element, not the document: one childList
 * observer per cart surface, no subtree, no attribute noise.
 */
(function () {
  'use strict';

  var VIEWPORT = '.cart-upsell__viewport';
  var TRACK = '[data-upsell-track]';

  function lineKeyOf(el) {
    var row = el.closest('[data-cart-item]');
    if (!row) { return null; }
    var input = row.querySelector('[data-update-cart]');
    return input ? input.getAttribute('data-update-cart') : null;
  }

  /* A key that survives the wipe. Line keys (variant:hash) are stable across a
     quantity change — measured — and an upsell card is identified by its
     product URL, which is the only thing about it that cannot drift. */
  function keyFor(el) {
    if (!el || el.nodeType !== 1 || !el.closest) { return null; }
    if (el.closest('.cart-upsell__card')) {
      var link = el.closest('.cart-upsell__card').querySelector('.cart-upsell__card__title');
      var href = link ? link.getAttribute('href') : '';
      if (el.matches('.cart-upsell__card__btn')) { return 'u-add|' + href; }
      if (el.matches('.cart-upsell__card__title')) { return 'u-title|' + href; }
      return null;
    }
    if (el.matches('[data-upsell-prev]')) { return 'u-prev|'; }
    if (el.matches('[data-upsell-next]')) { return 'u-next|'; }
    var k = lineKeyOf(el);
    if (!k) { return null; }
    if (el.matches('[data-increase-quantity]')) { return 'q-plus|' + k; }
    if (el.matches('[data-decrease-quantity]')) { return 'q-minus|' + k; }
    if (el.matches('[data-quantity-input]')) { return 'q-input|' + k; }
    if (el.matches('[data-freegift-open]')) { return 'q-gift|' + k; }
    if (el.closest('[data-remove-key]')) { return 'q-remove|' + k; }
    return null;
  }

  function rowFor(root, key) {
    try { return root.querySelector('[data-update-cart="' + key + '"]'); } catch (e) { return null; }
  }

  function findByKey(root, key) {
    if (!key) { return null; }
    var cut = key.indexOf('|');
    var kind = key.slice(0, cut);
    var id = key.slice(cut + 1);
    if (kind === 'u-prev') { return root.querySelector('[data-upsell-prev]'); }
    if (kind === 'u-next') { return root.querySelector('[data-upsell-next]'); }
    if (kind === 'u-add' || kind === 'u-title') {
      var links = root.querySelectorAll('.cart-upsell__card__title');
      for (var i = 0; i < links.length; i += 1) {
        if (links[i].getAttribute('href') === id) {
          if (kind === 'u-title') { return links[i]; }
          var card = links[i].closest('.cart-upsell__card');
          return card ? card.querySelector('.cart-upsell__card__btn') : null;
        }
      }
      return null;
    }
    var input = rowFor(root, id);
    var row = input ? input.closest('[data-cart-item]') : null;
    if (!row) { return null; }
    if (kind === 'q-input') { return input; }
    if (kind === 'q-plus') { return row.querySelector('[data-increase-quantity]'); }
    if (kind === 'q-minus') { return row.querySelector('[data-decrease-quantity]'); }
    if (kind === 'q-gift') { return row.querySelector('[data-freegift-open]'); }
    if (kind === 'q-remove') { return row.querySelector('[data-remove-key]'); }
    return null;
  }

  function signature(root) {
    var links = root.querySelectorAll('.cart-upsell__card__title');
    var out = [];
    for (var i = 0; i < links.length; i += 1) { out.push(links[i].getAttribute('href')); }
    return out.join('|');
  }

  function watch(root) {
    if (!root || root.__dcPreserve) { return; }
    root.__dcPreserve = true;

    var vpTop = 0;
    var trackLeft = 0;
    var focusKey = null;
    var sig = signature(root);

    /* scroll does not bubble, but it DOES capture — one capturing listener on
       the container catches the desktop panel and the mobile track both, and
       records the value while the nodes still exist. Reading it in the
       observer would be too late: the old nodes are detached by then, and a
       detached element reports scrollTop 0. */
    root.addEventListener('scroll', function (e) {
      var t = e.target;
      if (!t || t.nodeType !== 1 || !t.matches) { return; }
      if (t.matches(VIEWPORT)) { vpTop = t.scrollTop; }
      else if (t.matches(TRACK)) { trackLeft = t.scrollLeft; }
    }, true);

    root.addEventListener('focusin', function (e) { focusKey = keyFor(e.target); });

    /* The callback runs at the microtask checkpoint — after theme.js's whole
       .then() body (showForm / initQuantity / initQuickview / updateTotal) and
       still before the next paint. That is what lets `animation = 'none'` land
       before the first frame: measured minimum card opacity stays 1, so there
       is no flash at all, rather than a suppressed-after-the-fact one. */
    new MutationObserver(function () {
      var fresh = signature(root);
      var unchanged = fresh === sig;
      sig = fresh;

      if (unchanged) {
        var cards = root.querySelectorAll('.cart-upsell__card');
        for (var i = 0; i < cards.length; i += 1) { cards[i].style.animation = 'none'; }
        /* Le PANNEAU, lui, n'est pas traité ici. Son glissement d'arrivée (dc-upsell-in) ne peut pas
           être discriminé par la signature : à l'OUVERTURE du tiroir aussi, theme.js reconstruit un
           panneau identique, donc « inchangé » est vrai dans les deux cas et supprimer l'animation ici
           la supprimait précisément quand on la voulait. Le tri se fait par la classe dc-upsell-enter,
           posée sur #drawer-cart à l'ouverture seulement — voir plus bas dans ce fichier. */
        var vp = root.querySelector(VIEWPORT);
        if (vp && vpTop) { vp.scrollTop = vpTop; }
        var track = root.querySelector(TRACK);
        if (track && trackLeft) { track.scrollLeft = trackLeft; }
      }

      /* Only ever claim focus back from <body>. If anything else already holds
         it — the "Article ajouté" popdown, the promo field the visitor started
         typing in during the ~700 ms — leave it alone. */
      var active = document.activeElement;
      if (active && active !== document.body && active !== document.documentElement) { return; }
      var target = findByKey(root, focusKey);
      if (target) { target.focus({ preventScroll: true }); }
      else if (focusKey) {
        /* The control the visitor was on is gone (line removed, or an upsell
           card that is now in the cart). Land on the list itself rather than
           dumping focus at the top of the document. */
        root.setAttribute('tabindex', '-1');
        root.focus({ preventScroll: true });
        focusKey = null;
      }
    }).observe(root, { childList: true });
  }

  function boot() {
    var nodes = document.querySelectorAll('[data-line-items]');
    for (var i = 0; i < nodes.length; i += 1) { watch(nodes[i]); }
  }

  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', boot); }
  else { boot(); }
  document.addEventListener('shopify:section:load', boot);

  /*
   * FENÊTRE D'ANIMATION DU PANNEAU DE SUGGESTIONS.
   *
   * Le panneau glisse à l'arrivée quand on ouvre le tiroir. Le déclencheur ne peut être ni une
   * transition CSS ni la seule insertion du nœud :
   *
   *   • transition — elle ne joue jamais. À l'ouverture, theme.js rappelle loadHTML() et remplace tout
   *     l'innerHTML de [data-line-items], panneau compris. Un élément inséré alors que la classe
   *     d'état est déjà posée naît à l'état final, sans transition. Mesuré sur cinq images entre 80 et
   *     900 ms : opacité 1 du début à la fin.
   *   • insertion seule — une animation par keyframes joue bien à l'insertion, mais le panneau est
   *     réinséré à CHAQUE changement de quantité. Le geste d'ouverture devenait un tic à chaque clic
   *     sur « + ».
   *
   * D'où cette fenêtre : la classe n'est posée qu'à l'ouverture, et retirée une fois l'animation
   * passée. Le panneau reconstruit pendant la fenêtre s'anime ; tous ceux d'après, non.
   *
   * Le délai de retrait couvre le retard de loadHTML (~700 ms mesuré) plus la durée de l'animation
   * (560 ms + 200 ms de retard), avec une marge. Trop court, l'ouverture ne s'anime pas sur une
   * connexion lente ; trop long, la première modification de quantité rejoue le glissement.
   *
   * theme:drawer:open est dispatché SANS bulles par le thème : l'écoute doit être sur l'élément.
   */
  var ENTER_CLASS = 'dc-upsell-enter';
  var ENTER_WINDOW_MS = 2400;
  var enterTimer = null;

  function armUpsellEnter() {
    var drawer = document.getElementById('drawer-cart');
    if (!drawer) { return; }
    if (enterTimer) { clearTimeout(enterTimer); }
    drawer.classList.add(ENTER_CLASS);
    enterTimer = setTimeout(function () {
      drawer.classList.remove(ENTER_CLASS);
      enterTimer = null;
    }, ENTER_WINDOW_MS);
  }

  (function bindEnter() {
    var drawer = document.getElementById('drawer-cart');
    if (!drawer) { return; }
    drawer.addEventListener('theme:drawer:open', armUpsellEnter);
    drawer.addEventListener('theme:drawer:close', function () {
      if (enterTimer) { clearTimeout(enterTimer); enterTimer = null; }
      drawer.classList.remove(ENTER_CLASS);
    });
  })();
})();
